import React, { useState } from "react";
import { toast } from "react-hot-toast";
import {
  HiOutlineCog,
  HiPlus,
  HiTrash,
  HiPencil,
  HiExclamationCircle,
  HiChevronUp,
  HiChevronDown,
} from "react-icons/hi";
import QuestionBuilder from "./QuestionBuilder";
import QuestionCard from "./QuestionCard";
import QuestionTypeSelector from "./QuestionTypeSelector";
import questionnaireService from "../services/questionnaireService";
import { SECTION_TYPES } from "../models/types";
import {
  createQuestion,
  reorderItems,
  formatQuestionForAdmin,
} from "../utils/helpers";
import { motion, AnimatePresence } from "framer-motion";
import { FixedSizeList as List } from "react-window";

/**
 * SectionBuilder component
 * Allows editing a section and its questions
 */
const SectionBuilder = ({
  section,
  questionnaireId,
  onUpdateSection,
  onDeleteSection,
  isRequiredBrandInfo,
  dragHandleProps,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedQuestionType, setSelectedQuestionType] = useState(null);
  const [title, setTitle] = useState(section.title);
  const [description, setDescription] = useState(section.description);
  const [type, setType] = useState(section.type);
  const [expanded, setExpanded] = useState(false);
  const [showQuestionTypeSelector, setShowQuestionTypeSelector] =
    useState(false);

  // Handle save section edits
  const handleSaveSection = () => {
    onUpdateSection({
      ...section,
      title,
      description,
      type,
    });
    setIsEditing(false);
  };

  // Cancel section edits
  const handleCancelEdit = () => {
    setTitle(section.title);
    setDescription(section.description);
    setType(section.type);
    setIsEditing(false);
  };

  // Add a new question
  const handleAddQuestion = (questionType = null) => {
    // Prevent adding questions to the Brand Info section
    if (isRequiredBrandInfo) {
      toast.error(
        "Questions cannot be added to the Brand Info section as it contains standardized fields required for the brand profile."
      );
      return;
    }

    if (questionType) {
      // Direct question creation with type
      setSelectedQuestionType(questionType);
      setIsAddingQuestion(true);
      setEditingQuestion(null);
    } else {
      // Show question type selector
      setShowQuestionTypeSelector(true);
    }
  };

  // Handle question type selection
  const handleQuestionTypeSelect = (questionType) => {
    setSelectedQuestionType(questionType);
    setShowQuestionTypeSelector(false);
    setIsAddingQuestion(true);
    setEditingQuestion(null);
  };

  // Edit an existing question
  const handleEditQuestion = (question) => {
    // Prevent editing questions in the Brand Info section
    if (isRequiredBrandInfo) {
      toast.error(
        "Questions in the Brand Info section cannot be edited as they are standardized for the brand profile."
      );
      return;
    }

    setEditingQuestion(question);
    setIsAddingQuestion(true);
  };

  // Handle save question
  const handleSaveQuestion = async (questionData) => {
    try {
      if (editingQuestion) {
        // Update existing question
        await questionnaireService.updateQuestion(
          questionnaireId,
          section.id,
          editingQuestion.id,
          questionData
        );
        toast.success("Question updated successfully");
      } else {
        // Add new question
        const maxOrder = section.questions
          ? Math.max(...section.questions.map((q) => q.order), -1)
          : -1;

        const newQuestion = createQuestion(
          section.id,
          questionData.type,
          questionData.title,
          {
            ...questionData,
            order: maxOrder + 1,
          }
        );

        await questionnaireService.addQuestion(
          questionnaireId,
          section.id,
          newQuestion
        );
        toast.success("Question added successfully");
      }

      // Update the section in the parent component
      const updatedQuestionnaire =
        await questionnaireService.getQuestionnaireById(questionnaireId);
      const updatedSection = updatedQuestionnaire.sections.find(
        (s) => s.id === section.id
      );

      if (updatedSection) {
        onUpdateSection(updatedSection);
      }

      setIsAddingQuestion(false);
      setEditingQuestion(null);
      setSelectedQuestionType(null);
    } catch (error) {
      console.error("Error saving question:", error);
      toast.error("Failed to save question");
    }
  };

  // Handle duplicate question
  const handleDuplicateQuestion = async (question) => {
    try {
      const maxOrder = section.questions
        ? Math.max(...section.questions.map((q) => q.order), -1)
        : -1;

      const duplicatedQuestion = {
        ...question,
        id: undefined, // Let Firestore generate new ID
        title: `${question.title} (Copy)`,
        order: maxOrder + 1,
      };

      await questionnaireService.addQuestion(
        questionnaireId,
        section.id,
        duplicatedQuestion
      );

      // Update the section in the parent component
      const updatedQuestionnaire =
        await questionnaireService.getQuestionnaireById(questionnaireId);
      const updatedSection = updatedQuestionnaire.sections.find(
        (s) => s.id === section.id
      );

      if (updatedSection) {
        onUpdateSection(updatedSection);
      }

      toast.success("Question duplicated successfully");
    } catch (error) {
      console.error("Error duplicating question:", error);
      toast.error("Failed to duplicate question");
    }
  };

  // Handle delete question
  const handleDeleteQuestion = async (questionId) => {
    // Prevent deletion of questions in the Brand Info section
    if (isRequiredBrandInfo) {
      toast.error(
        "Questions in the Brand Info section cannot be deleted as they are required for the brand profile."
      );
      return;
    }

    if (!window.confirm("Are you sure you want to delete this question?")) {
      return;
    }

    try {
      await questionnaireService.deleteQuestion(
        questionnaireId,
        section.id,
        questionId
      );

      // Update the section in the parent component
      const updatedQuestionnaire =
        await questionnaireService.getQuestionnaireById(questionnaireId);
      const updatedSection = updatedQuestionnaire.sections.find(
        (s) => s.id === section.id
      );

      if (updatedSection) {
        onUpdateSection(updatedSection);
      }

      toast.success("Question deleted successfully");
    } catch (error) {
      console.error("Error deleting question:", error);
      toast.error("Failed to delete question");
    }
  };

  // Handle drag and drop reordering of questions
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    // Reorder questions array
    const reorderedQuestions = reorderItems(
      section.questions,
      source.index,
      destination.index
    );

    // Update section with reordered questions
    try {
      await questionnaireService.updateSection(questionnaireId, section.id, {
        ...section,
        questions: reorderedQuestions,
      });

      // Update the section in the parent component
      onUpdateSection({
        ...section,
        questions: reorderedQuestions,
      });

      toast.success("Questions reordered successfully");
    } catch (error) {
      console.error("Error reordering questions:", error);
      toast.error("Failed to reorder questions");
    }
  };

  // Handle moving question up
  const handleMoveQuestionUp = async (questionIndex) => {
    if (questionIndex === 0) return; // Already at top

    try {
      const reorderedQuestions = reorderItems(
        section.questions,
        questionIndex,
        questionIndex - 1
      );

      await questionnaireService.updateSection(questionnaireId, section.id, {
        ...section,
        questions: reorderedQuestions,
      });

      onUpdateSection({
        ...section,
        questions: reorderedQuestions,
      });

      toast.success("Question moved up");
    } catch (error) {
      console.error("Error moving question up:", error);
      toast.error("Failed to move question up");
    }
  };

  // Handle moving question down
  const handleMoveQuestionDown = async (questionIndex) => {
    if (questionIndex === section.questions.length - 1) return; // Already at bottom

    try {
      const reorderedQuestions = reorderItems(
        section.questions,
        questionIndex,
        questionIndex + 1
      );

      await questionnaireService.updateSection(questionnaireId, section.id, {
        ...section,
        questions: reorderedQuestions,
      });

      onUpdateSection({
        ...section,
        questions: reorderedQuestions,
      });

      toast.success("Question moved down");
    } catch (error) {
      console.error("Error moving question down:", error);
      toast.error("Failed to move question down");
    }
  };

  const renderQuestions = () => {
    const questions = section.questions || [];

    if (questions.length > 50) {
      const handleRow = ({ index, style }) => {
        const q = questions[index];
        return (
          <div style={style} key={q.id} className="pr-2">
            <QuestionCard
              key={q.id}
              question={q}
              onEdit={handleEditQuestion}
              onDelete={handleDeleteQuestion}
              onDuplicate={handleDuplicateQuestion}
              onUpdate={handleSaveQuestion}
            />
          </div>
        );
      };

      return (
        <List
          height={600}
          itemCount={questions.length}
          itemSize={120}
          width="100%"
          overscanCount={5}
        >
          {handleRow}
        </List>
      );
    }

    // Use QuestionCard with up/down buttons for reordering
    return (
      <div className="space-y-4">
        {questions.map((question, index) => (
          <div key={question.id} className="relative">
            {/* Reorder buttons */}
            <div className="absolute left-0 top-0 bottom-0 w-8 flex flex-col items-center justify-center bg-neutral-700/50 rounded-l-lg">
              <button
                onClick={() => handleMoveQuestionUp(index)}
                disabled={index === 0}
                className={`p-1 rounded ${
                  index === 0
                    ? "text-neutral-600 cursor-not-allowed"
                    : "text-neutral-400 hover:text-white hover:bg-neutral-600"
                }`}
                title="Move up"
              >
                <HiChevronUp size={14} />
              </button>
              <button
                onClick={() => handleMoveQuestionDown(index)}
                disabled={index === questions.length - 1}
                className={`p-1 rounded ${
                  index === questions.length - 1
                    ? "text-neutral-600 cursor-not-allowed"
                    : "text-neutral-400 hover:text-white hover:bg-neutral-600"
                }`}
                title="Move down"
              >
                <HiChevronDown size={14} />
              </button>
            </div>

            <div className="ml-8">
              <QuestionCard
                question={question}
                onEdit={handleEditQuestion}
                onDelete={handleDeleteQuestion}
                onDuplicate={handleDuplicateQuestion}
                onUpdate={handleSaveQuestion}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="border border-neutral-700 rounded-lg overflow-hidden bg-neutral-800">
      {/* Section header */}
      <div className="flex items-center justify-between p-4 bg-neutral-800 border-b border-neutral-700">
        <div className="flex items-center gap-3">
          <div
            {...dragHandleProps}
            className="cursor-grab text-neutral-400 hover:text-white"
          >
            <HiOutlineCog size={20} />
          </div>

          <div>
            <h3 className="text-lg font-medium text-white">{section.title}</h3>
            <p className="text-sm text-neutral-400">{section.description}</p>
            <p className="text-xs text-neutral-500">Type: {section.type}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isRequiredBrandInfo && (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-full"
                title="Edit section"
              >
                <HiPencil size={18} />
              </button>

              <button
                onClick={onDeleteSection}
                className="p-2 text-red-400 hover:bg-red-500/20 rounded-full"
                title="Delete section"
              >
                <HiTrash size={18} />
              </button>
            </>
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 text-neutral-400 hover:bg-neutral-700 rounded-full ml-2"
          >
            <span className="text-xs font-medium">
              {expanded ? "Collapse" : "Expand"}
            </span>
          </button>
        </div>
      </div>

      {/* Brand Info section guidance */}
      {isRequiredBrandInfo && (
        <div className="m-4 bg-blue-600/20 border border-blue-600/30 rounded-md p-3">
          <div className="flex items-start">
            <HiExclamationCircle className="text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-300 font-medium">
                Brand Info Section (Required)
              </p>
              <p className="text-xs text-blue-400 mt-1">
                This section is mandatory and contains standardized fields
                needed for brand visualization. All fields should be filled out
                for optimal visualization.
              </p>
              <p className="text-xs text-blue-400 mt-1">
                The fields in this section map to the following in the brand
                profile:
              </p>
              <ul className="text-xs text-blue-400 mt-1 list-disc list-inside">
                <li>"What is your company name?" → Brand Name</li>
                <li>"Briefly describe what your company does" → Description</li>
                <li>"What industry are you in?" → Displayed in Profile</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Section edit form */}
      {isEditing && (
        <div className="p-4 bg-neutral-700/30 border-b border-neutral-700">
          <h4 className="text-base font-medium mb-3">Edit Section</h4>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-1">
                Section Type
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={isRequiredBrandInfo}
                className={`w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green ${
                  isRequiredBrandInfo
                    ? "opacity-60 cursor-not-allowed"
                    : ""
                }`}
              >
                {Object.entries(SECTION_TYPES)
                  .filter(
                    ([, value]) =>
                      isRequiredBrandInfo || value !== SECTION_TYPES.BRAND_INFO
                  )
                  .map(([key, value]) => (
                    <option key={value} value={value}>
                      {key.replace(/_/g, " ")}
                    </option>
                  ))}
              </select>
              {isRequiredBrandInfo && (
                <p className="text-xs text-blue-400 mt-1">
                  The Brand Info section type cannot be changed.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-1">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green h-20"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1.5 border border-neutral-600 text-neutral-300 rounded-md hover:bg-neutral-700 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSection}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                disabled={!title}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Questions list and editor */}
      {expanded && (
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-base font-medium">Questions</h4>
            {!isRequiredBrandInfo && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleAddQuestion()}
                  className="flex items-center gap-1 px-3 py-1.5 bg-primary-green text-black rounded-md hover:bg-primary-green/90 transition text-sm"
                >
                  <HiPlus size={16} />
                  <span>Add Question</span>
                </button>
              </div>
            )}
          </div>

          {/* Question Type Selector Modal */}
          {showQuestionTypeSelector && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-neutral-800 rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-y-auto border border-neutral-700">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-white">
                    Choose Question Type
                  </h3>
                  <button
                    onClick={() => setShowQuestionTypeSelector(false)}
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    ✕
                  </button>
                </div>
                <QuestionTypeSelector onSelectType={handleQuestionTypeSelect} />
              </div>
            </div>
          )}

          {/* Question Editor */}
          {isAddingQuestion && (
            <div className="mb-6">
              <QuestionBuilder
                question={editingQuestion}
                selectedType={selectedQuestionType}
                onSave={handleSaveQuestion}
                onCancel={() => {
                  setIsAddingQuestion(false);
                  setEditingQuestion(null);
                  setSelectedQuestionType(null);
                }}
              />
            </div>
          )}

          {/* Questions List */}
          {section.questions && section.questions.length > 0 ? (
            renderQuestions()
          ) : (
            <div className="p-8 bg-neutral-700/30 rounded-md text-neutral-400 text-center">
              <div className="space-y-2">
                <p className="text-lg">No questions yet</p>
                <p className="text-sm">
                  Click "Add Question" to create your first question
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionBuilder;
